"use client";

import axios from "axios";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import Spinner from "@/ui/Spinner";
import { Emphasis } from "@/ui/atoms/Emphasis";
import { COLLECTIONS, type CollectionMeta } from "@/lib/collections";
import { type ImageSummary } from "@/ui/types";

const API_URL = process.env.NEXT_PUBLIC_MICROGRAPHER_API_URL;
const API_KEY = process.env.NEXT_PUBLIC_MICROGRAPHER_API_KEY;
const CDN = "https://d127u13u6yy1k7.cloudfront.net/static/images/products";

const imgUrl = (p: ImageSummary) =>
	`${CDN}/${p.img_name}/${p.img_name}-${p.orientation}-no-frame-no-mat.webp`;

// /collections — an editorial index of every collection (DESIGN.md §3, R.3b.1).
// Live collections are presented as full alternating "shows" (cover image + curatorial statement);
// forthcoming collections are teased in a quiet grid below.
export default function CollectionsPage() {
	const [products, setProducts] = useState<ImageSummary[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		axios
			.get<ImageSummary[]>(`${API_URL}/products`, { headers: { "x-api-key": API_KEY } })
			.then((res) => setProducts(Array.isArray(res.data) ? res.data : []))
			.catch((e) => console.error("Error fetching products:", e))
			.finally(() => setIsLoading(false));
	}, []);

	// Map each collection to a cover image (first piece in that series) + piece count.
	const covers = useMemo(() => {
		const map = new Map<string, { cover?: ImageSummary; count: number }>();
		for (const p of products) {
			if (!p.series) continue;
			const entry = map.get(p.series) ?? { count: 0 };
			if (!entry.cover) entry.cover = p;
			entry.count += 1;
			map.set(p.series, entry);
		}
		return map;
	}, [products]);

	const live = COLLECTIONS.filter((c) => !c.forthcoming && (covers.get(c.name)?.count ?? 0) > 0);
	const forthcoming = COLLECTIONS.filter((c) => c.forthcoming || (covers.get(c.name)?.count ?? 0) === 0);

	return (
		<main className="bg-bone text-mg-black">
			{/* Editorial intro */}
			<section className="mx-auto max-w-gallery px-5 py-section sm:px-8">
				<p className="label-caps mb-6 text-mg-gold-deep">The Collections</p>
				<h1 className="max-w-4xl font-serif text-display-lg font-light leading-[1.05] text-mg-black">
					Colour, <Emphasis mode="italic">grown</Emphasis> from matter.
				</h1>
				<p className="mt-8 max-w-prose text-lg leading-relaxed text-ink-muted">
					The work is organised into collections — small, themed bodies of images, each gathered around a
					corner of ordinary life. A medicine cabinet. A morning cup. The bright acids of citrus. Wander
					through them below.
				</p>
			</section>

			{isLoading ? (
				<div className="flex justify-center py-section">
					<Spinner />
				</div>
			) : (
				<>
					{/* Live collections as alternating "shows" */}
					<div className="border-t border-mg-black/10">
						{live.map((c, i) => (
							<CollectionShow
								key={c.slug}
								collection={c}
								cover={covers.get(c.name)?.cover}
								count={covers.get(c.name)?.count ?? 0}
								flip={i % 2 === 1}
							/>
						))}
					</div>

					{/* Forthcoming */}
					{forthcoming.length ? (
						<section className="mx-auto max-w-gallery px-5 py-section sm:px-8">
							<p className="label-caps mb-10 text-ink-muted">Forthcoming</p>
							<ul className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
								{forthcoming.map((c) => (
									<li key={c.slug} className="border-t border-mg-black/10 pt-5">
										<h3 className="font-serif text-xl font-light text-mg-black">{c.name}</h3>
										<p className="mt-2 text-sm leading-relaxed text-ink-muted">{c.statement}</p>
										<p className="label-caps mt-3 text-ink-faint">Arriving soon</p>
									</li>
								))}
							</ul>
						</section>
					) : null}
				</>
			)}
		</main>
	);
}

// A single live collection presented as a full-width "show": large cover image + curatorial text,
// alternating sides on desktop.
function CollectionShow({
	collection,
	cover,
	count,
	flip,
}: {
	collection: CollectionMeta;
	cover?: ImageSummary;
	count: number;
	flip: boolean;
}) {
	return (
		<section className="border-b border-mg-black/10">
			<div className="mx-auto grid max-w-gallery items-stretch gap-0 px-0 lg:grid-cols-2">
				{/* Image */}
				<Link
					href={`/collections/${collection.slug}`}
					className={`group relative block min-h-[52vh] overflow-hidden bg-bone-deep lg:min-h-[70vh] ${
						flip ? "lg:order-2" : ""
					}`}
				>
					{cover ? (
						// ASSET: ideally a dedicated collection cover (an everyday↔specimen pairing, §5);
						// falls back to the first piece in the series.
						<Image
							src={imgUrl(cover)}
							alt={collection.name}
							fill
							sizes="(min-width: 1024px) 50vw, 100vw"
							className="object-cover transition-transform duration-700 ease-gallery group-hover:scale-[1.02]"
						/>
					) : null}
				</Link>

				{/* Text */}
				<div
					className={`flex flex-col justify-center px-5 py-16 sm:px-12 lg:py-24 ${flip ? "lg:order-1" : ""}`}
				>
					<p className="label-caps mb-5 text-ink-muted">
						{count} {count === 1 ? "work" : "works"}
					</p>
					<h2 className="font-serif text-display font-light leading-tight text-mg-black">
						{collection.name}
					</h2>
					<p className="mt-6 max-w-md leading-relaxed text-ink-muted">{collection.statement}</p>
					<Link
						href={`/collections/${collection.slug}`}
						className="label-caps mt-8 inline-flex items-center gap-2 text-mg-black underline decoration-mg-gold-deep/50 underline-offset-4 transition-colors hover:decoration-mg-gold-deep"
					>
						View the collection
					</Link>
				</div>
			</div>
		</section>
	);
}
